import * as React from 'react';
import { useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

interface Position {
  title: string;
  category: string;
  description: string;
  requirements: string[];
}

const communityPositions: Position[] = [
  {
    title: 'Community Manager',
    category: 'Community & Social Media',
    description: `As a Community Manager, you will be responsible for nurturing and growing our user community. 
    You'll engage with users across multiple platforms, respond to their questions, 
    organize community events, and ensure a positive environment. This role is vital for 
    maintaining a strong connection between our product and its users.`,
    requirements: [
      '1+ years experience managing online communities or social groups',
      'Excellent communication and interpersonal skills',
      'Familiarity with Discord, Telegram, Twitter, and other social platforms',
      'Ability to handle conflict resolution and maintain brand voice',
    ],
  },
  {
    title: 'Social Media Specialist',
    category: 'Community & Social Media',
    description: `As a Social Media Specialist, you’ll take charge of our social presence. 
    Your day-to-day includes content creation, scheduling posts, tracking engagement metrics, 
    and using insights to guide future campaigns. If you love storytelling, building brand identity,
    and using data-driven approaches to grow audiences, we’d love to have you onboard.`,
    requirements: [
      '1+ years of experience in social media content creation and management',
      'Proficiency in using social media analytics tools',
      'Strong writing and creative thinking skills',
      'Knowledge of best practices for growth and engagement on popular platforms',
    ],
  },
];

const developerPositions: Position[] = [
  {
    title: 'Solana Blockchain Developer',
    category: 'Developer Roles',
    description: `As a Solana Blockchain Developer, you will be at the forefront of integrating and advancing our decentralized infrastructure on the Solana network. 
    Your work will involve writing and optimizing smart contracts in Rust, leveraging Solana’s high-throughput architecture for efficient transaction processing. 
    Additionally, you’ll develop and maintain protocols that enable secure, decentralized hardware resource sharing, ensuring the scalability and performance of AI workloads across a distributed network.`,
    requirements: [
      '3+ years of experience in blockchain development with a focus on Solana',
      'Proficiency in Rust and understanding of Solana-specific program development',
      'Deep knowledge of Solana’s runtime, smart contract architecture, and tooling (e.g., Anchor, Solana CLI)',
      'Familiarity with decentralized resource sharing protocols and incentives for distributed hardware networks',
      'Strong understanding of on-chain security best practices and smart contract audits',
    ],
  },
  {
    title: 'Frontend Developer',
    category: 'Developer Roles',
    description: `As a Frontend Developer, you’ll focus on building intuitive, responsive, and user-friendly interfaces. 
    You’ll translate designs into functional code, maintain a consistent UI/UX, and collaborate closely with 
    product and design teams. Your work ensures our users have an exceptional experience.`,
    requirements: [
      '2+ years of experience with React, TypeScript, and front-end frameworks',
      'Proficiency in CSS-in-JS solutions (MUI, Styled Components, etc.)',
      'Knowledge of UX and responsive design principles',
      'Familiarity with performance optimization and testing tools',
    ],
  },
  {
    title: 'Mobile Developer',
    category: 'Developer Roles',
    description: `As a Mobile Developer, you’ll build cutting-edge mobile applications for both Android and iOS. 
    You’ll focus on performance, scalability, and an intuitive user interface, ensuring that our users enjoy 
    a seamless experience on-the-go.`,
    requirements: [
      '2+ years of experience with React Native',
      'Understanding of mobile performance optimization',
      'Proficiency in mobile-specific testing and debugging tools',
      'Familiarity with app store deployment and CI/CD pipelines',
    ],
  },
];

const StyledSection = styled('section')(({ theme }) => ({
  padding: theme.spacing(16, 0),
  backgroundColor: theme.palette.background.paper,
}));

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: 500,
  bgcolor: 'background.default',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

export default function Careers() {
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    linkedin: ''
  });
  const [openModal, setOpenModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [field]: event.target.value });
  };

  const handleApplyClick = (position: Position) => {
    setSelectedPosition(position);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPosition(null);
    setFormValues({
      name: '',
      email: '',
      linkedin: ''
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedPosition) {
      alert('No position selected.');
      return;
    }

    const applicationData = {
      position: selectedPosition.title,
      name: formValues.name,
      emailAddress: formValues.email,
      linkedinUrl: formValues.linkedin
    };

    try {
      const response = await axios.post('https://api.vivachain.ai/v1/jobs/apply', applicationData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        alert('Your application has been submitted successfully!');
        handleCloseModal();
      } else {
        console.error('Error submitting application:', response.data);
        alert('There was an issue submitting your application. Please try again.');
      }
    } catch (error: any) {
      console.error('Error submitting application:', error);
      alert('There was an error submitting your application. Please try again later.');
    }
  };

  return (
    <StyledSection id="careers">
      <Container>
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 8 },
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
            Join Our Team
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, maxWidth: 600, mx: 'auto' }}>
            Help shape the future of autonomous AI. We are looking for talented individuals who are passionate about 
            community building, cutting-edge technology, and innovative solutions.
          </Typography>
        </Box>

        {/* Developer Roles */}
        <Box sx={{ mb: 8 }}>
          <Grid container spacing={4}>
            {developerPositions.map((pos, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {pos.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {pos.description}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Requirements:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                      {pos.requirements.map((req, idx) => (
                        <li key={idx}>
                          <Typography variant="body2" color="text.secondary">
                            {req}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Button size="small" variant="outlined" onClick={() => handleApplyClick(pos)}>
                      Apply Now
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Community & Social Media Roles */}
        <Box sx={{ mb: 8 }}>
          <Grid container spacing={4}>
            {communityPositions.map((pos, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {pos.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {pos.description}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Requirements:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                      {pos.requirements.map((req, idx) => (
                        <li key={idx}>
                          <Typography variant="body2" color="text.secondary">
                            {req}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Button size="small" variant="outlined" onClick={() => handleApplyClick(pos)}>
                      Apply Now
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Application Modal */}
        <Modal open={openModal} onClose={handleCloseModal} aria-labelledby="apply-modal-title" aria-describedby="apply-modal-description">
          <Box sx={modalStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography id="apply-modal-title" variant="h5" component="h2">
                Apply for {selectedPosition?.title}
              </Typography>
              <IconButton onClick={handleCloseModal}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Name"
                  variant="outlined"
                  fullWidth
                  required
                  value={formValues.name}
                  onChange={handleChange('name')}
                />
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  type="email"
                  value={formValues.email}
                  onChange={handleChange('email')}
                />
                <TextField
                  label="LinkedIn URL"
                  variant="outlined"
                  fullWidth
                  value={formValues.linkedin}
                  onChange={handleChange('linkedin')}
                />
                <Box sx={{ textAlign: 'center' }}>
                  <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 160 }}>
                    Submit Application
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Modal>
      </Container>
    </StyledSection>
  );
}